import {useForwardingRef} from '@rocket-ui/react-tools';
import {cloneEventWithNewValue, phone as phoneTool} from '@rocket-ui/tools';
import React, {forwardRef} from 'react';
import {MaskedInput} from './MaskedInput';
import {InputProps} from './types';

const knownMasks = {
	russia: {
		placeholder: '+7 000 000-00-00',
		mask: ['+', '7', ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/],
		startIndex: 3,
	},
	default: {
		placeholder: '',
		mask: [],
		startIndex: 1,
	},
};

export type PhoneInputProps = Omit<InputProps, 'value' | 'placeholder' | 'dynamicPlaceholder'> & {
	value?: string;
};

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
	{value, onChange, onPaste, onClick, onKeyDown, ...props},
	inputRef,
) {
	const ref = useForwardingRef(inputRef);

	const handleChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (phoneTool.digitsLength(event.target.value) > 14) return;

			onChange?.(cloneEventWithNewValue(event, phoneTool.sanitize(event.target.value)));
		},
		[onChange],
	);

	const handlePaste = React.useCallback(
		(event: React.ClipboardEvent<HTMLInputElement>) => {
			const value = phoneTool.sanitize(event.clipboardData.getData('Text'));

			if (typeof event.clipboardData.setData === 'function') event.clipboardData.setData('Text', value);

			onPaste?.(event);
		},
		[onPaste],
	);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		const currentValue = ref.current?.value ?? '';

		if (event.code === 'Backspace' && /^\+7 *$/.test(currentValue) && ref.current) {
			event.preventDefault();
			const cleanValue = currentValue.replace(/^\+7 *$/, '+');
			// @ts-ignore
			onChange?.(cloneEventWithNewValue(event, cleanValue));
		}

		onKeyDown?.(event);
	};

	const isRussianRegionCode = value?.startsWith('+7');
	const config = isRussianRegionCode ? knownMasks.russia : knownMasks.default;

	return (
		<MaskedInput
			ref={ref}
			mask={config.mask}
			value={value || '+'}
			onChange={handleChange}
			onPaste={handlePaste}
			onKeyDown={handleKeyDown}
			dynamicPlaceholder={config.placeholder}
			{...props}
		/>
	);
});
